import { defineStore } from 'pinia';
import { ref } from 'vue';
import { customAxiosInstance } from '@/common/api/utils/mutator';
import { i18n } from '@/common/locale';

/**
 * 챗봇 대화 목록 store.
 *
 * 대화 = 한 사용자 내 격리된 thread. 사용자는 여러 대화를 병행하고, 토글식 목록에서
 * 이전 대화를 이어가거나 새 대화를 생성하거나 항목을 삭제할 수 있다.
 *
 * 백엔드:
 *   - GET    /api/v9/chatbot/conversations
 *   - POST   /api/v9/chatbot/conversations
 *   - DELETE /api/v9/chatbot/conversations/{id}
 *   - GET    /api/v9/chatbot/conversations/{id}/messages
 *
 * 정렬: lastMessageAt DESC (백엔드 처리).
 * 활성 대화는 `activeId` 에 보관 — chat store 가 stream 요청 시 conversationId 로 사용.
 */
export interface Conversation {
  conversationId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string;
  lastMessagePreview: string;
  lastMessageSender: string;
  messageCount: number;
}

const LS_ACTIVE_KEY = 'chatbotActiveConversationId';

export const useChatConversationsStore = defineStore('chatConversations', () => {
  const list = ref<Conversation[]>([]);
  const activeId = ref<string | null>(loadActiveId());
  const isLoading = ref(false);
  const panelOpen = ref(false);
  const fetchError = ref<string | null>(null);
  /**
   * 대화 전환(새 대화 생성/선택/활성 삭제) 진행 중 — 완료까지 입력·전환 진입점을 모두 잠근다.
   * 공용 isLoading(boolean)을 잠금으로 쓰면 병렬 요청 중 먼저 끝난 쪽이 해제해 버리고,
   * history GET 의 finally 도 같은 잠금을 풀어 전환 중 발화가 이전 대화로 저장된다 (3차 리뷰 P1).
   */
  const switching = ref(false);

  function loadActiveId(): string | null {
    try {
      return localStorage.getItem(LS_ACTIVE_KEY);
    } catch {
      return null;
    }
  }
  function persistActiveId(id: string | null): void {
    try {
      if (id) localStorage.setItem(LS_ACTIVE_KEY, id);
      else localStorage.removeItem(LS_ACTIVE_KEY);
    } catch {
      /* ignore */
    }
  }

  async function fetchList(userId: string): Promise<void> {
    if (!userId) return;
    isLoading.value = true;
    fetchError.value = null;
    try {
      const res = await customAxiosInstance<{ data: Conversation[] }>({
        url: '/api/v9/chatbot/conversations',
        method: 'GET',
        params: { userId, limit: 50 },
      });
      list.value = res.data?.data || [];
    } catch (e) {
      fetchError.value = i18n.global.t('WORD.CHATBOT.CONVERSATION.FETCH_ERROR');
      console.warn('[chatbot] fetchList failed', e);
    } finally {
      isLoading.value = false;
    }
  }

  async function createNew(userId: string): Promise<string | null> {
    if (!userId) return null;
    try {
      const res = await customAxiosInstance<Conversation>({
        url: '/api/v9/chatbot/conversations',
        method: 'POST',
        data: { userId },
      });
      const created = res.data;
      if (created?.conversationId) {
        list.value = [created, ...list.value];
        setActiveId(created.conversationId);
        return created.conversationId;
      }
    } catch (e) {
      console.warn('[chatbot] createNew failed', e);
    }
    return null;
  }

  async function remove(userId: string, conversationId: string): Promise<boolean> {
    if (!userId || !conversationId) return false;
    try {
      await customAxiosInstance({
        url: `/api/v9/chatbot/conversations/${encodeURIComponent(conversationId)}`,
        method: 'DELETE',
        params: { userId },
      });
      // optimistic — 목록에서 제거
      list.value = list.value.filter((c) => c.conversationId !== conversationId);
      // 활성 대화 삭제 시 active 해제
      if (activeId.value === conversationId) {
        setActiveId(null);
      }
      return true;
    } catch (e) {
      console.warn('[chatbot] remove failed', e);
      return false;
    }
  }

  /**
   * 대화 메타 갱신 — stream 응답의 'conversation' 이벤트 / 발화 후에 호출하여
   * 목록의 항목 (title / lastMessageAt / preview / messageCount) 을 최신화.
   */
  function upsertConversation(c: Conversation): void {
    const idx = list.value.findIndex((x) => x.conversationId === c.conversationId);
    if (idx >= 0) {
      list.value.splice(idx, 1);
    }
    list.value = [c, ...list.value];
  }

  function setActiveId(id: string | null): void {
    activeId.value = id;
    persistActiveId(id);
  }

  function togglePanel(): void {
    panelOpen.value = !panelOpen.value;
  }
  function openPanel(): void {
    panelOpen.value = true;
  }
  function closePanel(): void {
    panelOpen.value = false;
  }

  return {
    list,
    activeId,
    isLoading,
    panelOpen,
    fetchError,
    switching,
    fetchList,
    createNew,
    remove,
    upsertConversation,
    setActiveId,
    togglePanel,
    openPanel,
    closePanel,
  };
});
